import { useEffect, useState } from 'react';
import notify from '../../Utils/notify';
import Breadcrumb from '../Breadcrumb/Breadcrumb';
import usePermisos from '../../hooks/usePermisos';
import NoPermissions from '../NoPermissions/NoPermissions';
import Loader from '../../Layout/Loaders/Loader';
import axios from '../../axios';
import { 
    TrashIcon, 
    FunnelIcon, 
    XMarkIcon,
    PhoneIcon,
    ChatBubbleBottomCenterTextIcon,
    CalendarDaysIcon,
    ClockIcon,
    UserIcon,
    InformationCircleIcon,
    BuildingOffice2Icon,
    PaperAirplaneIcon,
    PhotoIcon,
    SpeakerWaveIcon,
    DocumentIcon,
    VideoCameraIcon,
} from '@heroicons/react/24/outline';
import Table, { useTableSort, useTablePagination } from '../UI/Table';
import MediaViewerModal from './MediaViewerModal';
import AppointmentDetailModal from './AppointmentDetailModal';

const PERMISOS_REQUERIDOS = ['whatsapp_logs_read', 'whatsapp_logs_delete', 'whatsapp_reply'];

// Mini modal para enviar mensaje de respuesta
const ReplyModal = ({ to, nombre, onSend, onClose }) => {
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);

    const handleSend = async () => {
        if (!message.trim()) return;
        setSending(true);
        await onSend(to, message.trim());
        setSending(false);
    };

    // Ctrl+Enter envía el mensaje
    const handleKey = (e) => {
        if (e.key === 'Enter' && e.ctrlKey) handleSend();
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/40" onClick={onClose} />
            <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
                {/* Header estilo WhatsApp */}
                <div className="flex items-center gap-3 px-4 py-3 bg-[#075E54]">
                    <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
                        <UserIcon className="h-5 w-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-white font-semibold text-sm truncate">{nombre}</p>
                        <p className="text-white/70 text-xs font-mono">{to}</p>
                    </div>
                    <button onClick={onClose} className="text-white/70 hover:text-white transition-colors">
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>

                {/* Área de mensaje estilo WhatsApp */}
                <div className="px-4 pt-4 pb-3 bg-[#ECE5DD]">
                    <textarea
                        autoFocus
                        rows={4}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        onKeyDown={handleKey}
                        placeholder="Escribe un mensaje... (Ctrl+Enter para enviar)"
                        className="w-full resize-none rounded-lg px-3 py-2 text-sm bg-white border-0 focus:outline-none focus:ring-2 focus:ring-[#25D366] shadow-sm"
                        maxLength={4096}
                    />
                    <p className="text-right text-xs text-gray-400 mt-1">{message.length}/4096</p>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-2 px-4 py-3 bg-white border-t border-gray-100">
                    <button
                        onClick={onClose}
                        className="px-4 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={handleSend}
                        disabled={!message.trim() || sending}
                        className="flex items-center gap-2 px-4 py-1.5 text-sm bg-[#25D366] hover:bg-[#128C7E] disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium"
                    >
                        <PaperAirplaneIcon className="h-4 w-4" />
                        {sending ? 'Enviando...' : 'Enviar'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const BitacoraWhatsApp = () => {
    const { loading: loadingPermisos, tienePermiso, roles } = usePermisos(PERMISOS_REQUERIDOS);
    const today = new Date();
    const [selectedDay, setSelectedDay] = useState(today);
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [estadisticas, setEstadisticas] = useState(null);
    const [appointmentModal, setAppointmentModal] = useState(null);
    const [replyModal, setReplyModal] = useState(null); // { to, nombre }
    const [mediaModal, setMediaModal] = useState(null); // log con archivo multimedia a visualizar
    console.log('Permisos:', tienePermiso('whatsapp_reply'), 'Roles:', roles);
    // Filtros
    const [filtros, setFiltros] = useState({
        fecha_inicio: '',
        fecha_fin: '',
        event_type: '',
        from_number: ''
    });
    const [filtrosActivos, setFiltrosActivos] = useState(false);
    const [mostrarFiltros, setMostrarFiltros] = useState(false);
    
    // Tipos de eventos disponibles
    const [tiposEventos, setTiposEventos] = useState([]);
    
    // Hooks para tabla
    const { sortedData, sortConfig, handleSort } = useTableSort(logs, { field: 'created_at', direction: 'DESC' });
    const {
        paginatedData,
        page,
        setPage,
        itemsPerPage,
        setItemsPerPage,
        totalItems,
        totalPages
    } = useTablePagination(sortedData, { page: 1, itemsPerPage: 50 });

    // Cargar datos iniciales
    useEffect(() => {
        obtenerLogs();
        obtenerTiposEventos();
        obtenerEstadisticas();
    }, []);

    // Obtener logs con filtros
    const obtenerLogs = async (filtrosParam = filtros) => {
        console.log('Obteniendo logs con filtros:', filtrosParam);
        console.log('Filtros activos:', filtrosActivos);
        try {
            setLoading(true);
            const params = new URLSearchParams();
            
            if (filtrosParam.fecha_inicio) params.append('fecha_inicio', filtrosParam.fecha_inicio);
            if (filtrosParam.fecha_fin) params.append('fecha_fin', filtrosParam.fecha_fin);
            if (filtrosParam.event_type) params.append('event_type', filtrosParam.event_type);
            if (filtrosParam.from_number) params.append('from_number', filtrosParam.from_number);
            
            const response = await axios.get(`api/whatsapp/logs?${params.toString()}`);
            console.log('Logs obtenidos:', response.data); 
            setLogs(response.data || []);
            setFiltrosActivos(
                !!filtrosParam.fecha_inicio || 
                !!filtrosParam.fecha_fin || 
                !!filtrosParam.event_type || 
                !!filtrosParam.from_number
            );
        } catch (error) {
            console.error('Error al obtener logs:', error);
            notify.error('Error al cargar los registros de WhatsApp', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Obtener tipos de eventos para el select
    const obtenerTiposEventos = async () => {
        try {
            const response = await axios.get('api/whatsapp/event-types');
            setTiposEventos(response.data || []);
        } catch (error) {
            console.error('Error al obtener tipos de eventos:', error);
        }
    };

    // Obtener estadísticas
    const obtenerEstadisticas = async () => {
        try {
            const response = await axios.get('api/whatsapp/stats');
            setEstadisticas(response.data);
        } catch (error) {
            console.error('Error al obtener estadísticas:', error);
        }
    };

    // Aplicar filtros
    const aplicarFiltros = () => {
        setPage(1); // Resetear a página 1
        obtenerLogs(filtros);
    };

    // Limpiar filtros
    const limpiarFiltros = () => {
        const filtrosVacios = {
            fecha_inicio: '',
            fecha_fin: '',
            event_type: '',
            from_number: ''
        };
        setFiltros(filtrosVacios);
        setPage(1);
        obtenerLogs(filtrosVacios);
    };

    // Eliminar log
    const eliminarLog = async (id) => {
        if (!tienePermiso('whatsapp_logs_delete')) {
            notify('No tienes permisos para eliminar registros', 'warning');
            return;
        }

        if (!window.confirm('¿Estás seguro de eliminar este registro?')) {
            return;
        }

        try {
            await axios.delete(`api/whatsapp/logs/${id}`);
            notify.success('Registro eliminado correctamente', 'success');
            obtenerLogs();
            obtenerEstadisticas();
        } catch (error) {
            console.error('Error al eliminar log:', error);
            notify.error('Error al eliminar el registro', 'error');
        }
    };

    const enviarRespuesta = async (to, message) => {
        try {
            await axios.post('api/whatsapp/reply', { to, message });
            console.log('Respuesta enviada:', { to, message });
            notify.success('Mensaje enviado correctamente', 'success');
            setReplyModal(null);
        } catch (error) {
            const msg = error.response?.data?.message || 'Error al enviar el mensaje';
            console.error('Error al enviar respuesta:', error);
            notify.error(msg, 'error');
        }
    };

    // Obtener color del badge según tipo de evento
    const getBadgeColor = (eventType) => {
        const colors = {
            'mensaje_text': 'bg-cyan-100 text-cyan-800',
            'mensaje_image': 'bg-indigo-100 text-indigo-800',
            'mensaje_video': 'bg-purple-100 text-purple-800',
            'mensaje_document': 'bg-yellow-100 text-yellow-800',
            'sent': 'bg-green-100 text-green-600',
            'delivered': 'bg-gray-100 text-gray-600',
            'read': 'bg-blue-50 text-blue-600',
            'failed': 'bg-red-100 text-red-700',
        };
        return colors[eventType] || 'bg-gray-100 text-gray-800';
    };

    // SVG palomita simple (WhatsApp)
    const SingleCheck = ({ className }) => (
        <svg className={className} viewBox="0 0 16 15" fill="currentColor">
            <path d="M15.01 3.316l-.478-.372a.365.365 0 00-.51.063L8.666 9.879a.32.32 0 01-.484.033l-.358-.325a.319.319 0 00-.484.032l-.378.483a.418.418 0 00.036.541l1.32 1.266c.143.14.361.125.484-.033l6.272-8.048a.366.366 0 00-.064-.513z"/>
        </svg>
    );

    // SVG doble palomita (WhatsApp)
    const DoubleCheck = ({ className }) => (
        <svg className={className} viewBox="0 0 16 15" fill="currentColor">
            <path d="M15.01 3.316l-.478-.372a.365.365 0 00-.51.063L8.666 9.879a.32.32 0 01-.484.033l-.358-.325a.319.319 0 00-.484.032l-.378.483a.418.418 0 00.036.541l1.32 1.266c.143.14.361.125.484-.033l6.272-8.048a.366.366 0 00-.064-.513zm-4.1 0l-.478-.372a.365.365 0 00-.51.063L4.566 9.879a.32.32 0 01-.484.033L1.891 7.769a.366.366 0 00-.515.006l-.423.433a.364.364 0 00.006.514l3.258 3.185c.143.14.361.125.484-.033l6.272-8.048a.365.365 0 00-.063-.51z"/>
        </svg>
    );

    // Ícono estilo WhatsApp según el tipo de evento
    const getEventIcon = (eventType) => {
        switch (eventType) {
            case 'sent':
                return <SingleCheck className="w-4 h-4 text-gray-400 shrink-0" />;
            case 'delivered':
                return <DoubleCheck className="w-4 h-4 text-gray-400 shrink-0" />;
            case 'read':
                return <DoubleCheck className="w-4 h-4 text-[#53bdeb] shrink-0" />;
            case 'failed':
                return <XMarkIcon className="w-4 h-4 text-red-500 shrink-0" />;
            case 'mensaje_text':
            case 'mensaje_image':
            case 'mensaje_video':
            case 'mensaje_document':
                return <ChatBubbleBottomCenterTextIcon className="w-4 h-4 text-cyan-500 shrink-0" />;
            default:
                return null;
        }
    };

    // Formatear número de teléfono
    const formatearTelefono = (numero) => {
        if (!numero) return '-';
        // Si tiene 12 dígitos y empieza con 52 (México)
        if (numero.length === 12 && numero.startsWith('52')) {
            return `+${numero.slice(0, 2)} ${numero.slice(2)}`;
        }
        return numero.startsWith('+') ? numero : `+${numero}`;
    };

    // Formatear fecha
    const formatearFecha = (fecha) => {
        if (!fecha) return '-';
        const date = new Date(fecha);
        return date.toLocaleString('es-MX', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    // Extrae del raw_payload si el mensaje trae un archivo multimedia soportado por el visor
    const getMediaInfo = (row) => {
        const message = row?.raw_payload?.message;
        const mediaTypesSoportados = ['audio', 'image', 'video', 'document', 'sticker'];
        const type = message?.type;
        if (!message || !mediaTypesSoportados.includes(type)) return null;

        return {
            mediaType: type,
            caption: message[type]?.caption || null,
            filename: type === 'document' ? (message.document?.filename || null) : null,
        };
    };

    const MEDIA_BUTTON_CONFIG = {
        image: { icon: <PhotoIcon className="h-4 w-4" />, label: 'Ver imagen' },
        sticker: { icon: <PhotoIcon className="h-4 w-4" />, label: 'Ver sticker' },
        audio: { icon: <SpeakerWaveIcon className="h-4 w-4" />, label: 'Reproducir audio' },
        video: { icon: <VideoCameraIcon className="h-4 w-4" />, label: 'Ver video' },
        document: { icon: <DocumentIcon className="h-4 w-4" />, label: 'Ver documento' },
    };

    // Verificar permisos
    if (loadingPermisos) {
        return <Loader />;
    }

    if (!tienePermiso('whatsapp_logs_read')) {
        return <NoPermissions />;
    }

    // Columnas de la tabla
    const columns = [
        {
            key: 'id',
            label: 'ID',
            sortable: true,
            render: (value) => `${value}`
        },
        {
            key: 'created_at',
            label: 'Fecha Recepción',
            sortable: true,
            render: (value) => (
                <span className="whitespace-nowrap">📅 {formatearFecha(value)}</span>
            )
        },
        {
            key: 'event_type',
            label: 'Evento / Estado',
            sortable: true,
            render: (value) => (
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium capitalize ${getBadgeColor(value)}`}>
                    {getEventIcon(value)}
                    {value}
                </span>
            )
        },
        {
            key: 'from_number',
            label: 'Remitente',
            sortable: true,
            render: (value) => (
                <div className="flex items-center gap-2">
                    <span className="whitespace-nowrap">📱 {formatearTelefono(value)}</span>
                </div>
            )
        },
        {
            key: 'appointment_id',
            label: 'Cita ID',
            sortable: true,
            render: (_value, row) => {
                if (row?.appointment_id && row?.appointment) {
                    return (
                        <button
                            onClick={() => setAppointmentModal(row.appointment)}
                            className="text-left hover:opacity-80 transition-opacity cursor-pointer"
                        >
                            <div className="text-sm">
                                <div className="font-medium text-green-600 hover:underline">
                                    {row.appointment.nombre_paciente || 'Sin nombre'}
                                </div>
                                <div className="text-gray-400 text-xs">
                                    Cita #{row.appointment_id}
                                </div>
                            </div>
                        </button>
                    );
                }
                return <span className="text-gray-400 italic text-xs">No vinculado</span>;
            }
        },
        {
            key: 'message_content',
            label: 'Contenido / Respuesta',
            sortable: false,
            render: (value, row) => {
                const mediaInfo = getMediaInfo(row);

                if (mediaInfo) {
                    const config = MEDIA_BUTTON_CONFIG[mediaInfo.mediaType] || {};
                    return (
                        <div className="max-w-xs p-2 space-y-1">
                            <button
                                onClick={() => setMediaModal(row)}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-[#25D366]/10 text-[#128C7E] hover:bg-[#25D366]/20 transition-colors cursor-pointer"
                            >
                                {config.icon}
                                {mediaInfo.filename || config.label}
                            </button>
                            {mediaInfo.caption && (
                                <p className="text-xs text-gray-500 truncate">{mediaInfo.caption}</p>
                            )}
                        </div>
                    );
                }

                const content = value;
                if (!content) {
                    return <span className="text-gray-400 italic">(Sin texto)</span>;
                }
                const truncated = content.length > 160 ? content.substring(0, 160) + '...' : content;
                return (
                     <div className="max-w-xs p-2" >
                        <span className="text-sm">📝 "{truncated || '-'}"</span>
                    </div>
                );
            }
        },
        {
            key: 'actions',
            label: 'Acciones',
            sortable: false,
            render: (_value, row) => (
                <div className="flex items-center gap-2">
                    {tienePermiso('whatsapp_reply') && row.from_number && (
                        <button
                            onClick={() => setReplyModal({
                                to: row.from_number,
                                nombre: row.appointment?.nombre_paciente || row.from_number,
                            })}
                            className="text-[#25D366] hover:text-[#128C7E] transition-colors"
                            title="Responder"
                        >
                            <PaperAirplaneIcon className="h-5 w-5" />
                        </button>
                    )}
                    {tienePermiso('whatsapp_logs_delete') && (
                        <button
                            onClick={() => eliminarLog(row.id)}
                            className="text-red-600 hover:text-red-900 transition-colors"
                            title="Eliminar"
                        >
                            <TrashIcon className="h-5 w-5" />
                        </button>
                    )}
                </div>
            )
        }
    ];

    return(
    <>           
        {/* Modal detalle de cita */}
        {appointmentModal && (
            <AppointmentDetailModal
                appointment={appointmentModal}
                onClose={() => setAppointmentModal(null)}
            />
        )}

        {/* Mini modal de respuesta */}
        {replyModal && (
            <ReplyModal
                to={replyModal.to}
                nombre={replyModal.nombre}
                onSend={enviarRespuesta}
                onClose={() => setReplyModal(null)}
            />
        )}

        {/* Visor de imagen/audio/video/documento */}
        {mediaModal && (
            <MediaViewerModal
                log={mediaModal}
                onClose={() => setMediaModal(null)}
            />
        )}
        <div className="container-admin-menu">
             <Breadcrumb hasButton={tienePermiso('users_create')} hasFilters={false} />
        </div>  
        <div className="container-admin-container">
            <div className="container-admin bg-white rounded-lg shadow-md p-4">
                <div className="flex flex-col md:flex-row md:items-center info-badge rounded-xl p-4 shadow-sm mb-6">
                    <div className="flex flex-col md:flex-row md:items-center">
                        <h3>Bitacora WhatsApp</h3>
                        <img className="w-20" src="/images/whatsapp.png" alt="whatsapp" />
                    </div>
                    <div>
                        <h6 className="text-lg font-semibold text-white">
                            <span className="font-bold">Fecha:</span> {selectedDay.toLocaleDateString("es-MX", { year: 'numeric', month: 'long', day: 'numeric' })}
                        </h6>
                    </div>
                </div>

                {estadisticas && (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                        <div className="bg-white rounded-lg shadow p-4">
                            <div className="text-sm font-medium text-gray-500">Total Registros</div>
                            <div className="mt-2 text-3xl font-semibold text-gray-900">{estadisticas.total_logs}</div>
                        </div>
                        <div className="bg-white rounded-lg shadow p-4">
                            <div className="text-sm font-medium text-gray-500">Mensajes Entrantes</div>
                            <div className="mt-2 text-3xl font-semibold text-blue-600">{estadisticas.mensajes_entrantes}</div>
                        </div>
                        <div className="bg-white rounded-lg shadow p-4">
                            <div className="text-sm font-medium text-gray-500">Entregados</div>
                            <div className="mt-2 text-3xl font-semibold text-green-600">{estadisticas.mensajes_entregados}</div>
                        </div>
                        <div className="bg-white rounded-lg shadow p-4">
                            <div className="text-sm font-medium text-gray-500">Con Cita Vinculada</div>
                            <div className="mt-2 text-3xl font-semibold text-purple-600">{estadisticas.con_cita_vinculada}</div>
                        </div>
                    </div>
                )}

                {/* Filtros */}
                <div className="bg-white rounded-lg shadow mb-6">
                    <div className="px-4 py-3 border-b border-gray-200">
                        <button
                            onClick={() => setMostrarFiltros(!mostrarFiltros)}
                            className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                        >
                            <FunnelIcon className="h-5 w-5" />
                            Filtros
                            {filtrosActivos && (
                                <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded-full">
                                    Activos
                                </span>
                            )}
                        </button>
                    </div>

                    {mostrarFiltros && (
                        <div className="px-4 py-4">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                                {/* Fecha inicio */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Fecha Inicio
                                    </label>
                                    <input
                                        type="date"
                                        value={filtros.fecha_inicio}
                                        onChange={(e) => setFiltros({ ...filtros, fecha_inicio: e.target.value })}
                                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    />
                                </div>

                                {/* Fecha fin */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Fecha Fin
                                    </label>
                                    <input
                                        type="date"
                                        value={filtros.fecha_fin}
                                        onChange={(e) => setFiltros({ ...filtros, fecha_fin: e.target.value })}
                                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    />
                                </div>

                                {/* Tipo de evento */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Tipo de Evento
                                    </label>
                                    <select
                                        value={filtros.event_type}
                                        onChange={(e) => setFiltros({ ...filtros, event_type: e.target.value })}
                                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 browser-default"
                                    >
                                        <option value="">Todos</option>
                                        {tiposEventos.map((tipo) => (
                                            <option key={tipo} value={tipo}>
                                                {tipo}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Número de teléfono */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Número de Teléfono
                                    </label>
                                    <input
                                        type="text"
                                        value={filtros.from_number}
                                        onChange={(e) => setFiltros({ ...filtros, from_number: e.target.value })}
                                        placeholder="Ej: 5213329537709"
                                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    />
                                </div>
                            </div>

                            {/* Botones de acción */}
                            <div className="flex gap-2">
                                <button
                                    onClick={aplicarFiltros}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
                                >
                                    Aplicar Filtros
                                </button>
                                {filtrosActivos && (
                                    <button
                                        onClick={limpiarFiltros}
                                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors text-sm font-medium flex items-center gap-2"
                                    >
                                        <XMarkIcon className="h-4 w-4" />
                                        Limpiar
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Tabla */}
                <div className="bg-white rounded-lg shadow">
                    {loading ? (
                        <div className="p-8">
                            <Loader />
                        </div>
                    ) : logs.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">
                            <ChatBubbleBottomCenterTextIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                            <p>No se encontraron registros</p>
                        </div>
                    ) : (
                        <Table
                            columns={columns}
                            data={paginatedData}
                            sorting={{
                                field: sortConfig.field,
                                direction: sortConfig.direction,
                                onSort: handleSort,
                            }}
                            pagination={{
                                page,
                                totalItems,
                                itemsPerPage,
                                onPageChange: setPage,
                                onItemsPerPageChange: setItemsPerPage,
                            }}
                        />
                    )}
                </div>
            </div>
            
        </div>
      </>  
    );
};

export default BitacoraWhatsApp;
